// =============================================
// FLOW VALIDATOR
// =============================================
// Arquivo: FlowValidator.tsx
// Descrição: Valida fluxos e detecta erros/avisos
// =============================================

import { Node, Edge } from 'reactflow';

export interface ValidationIssue {
  id: string;
  type: 'error' | 'warning' | 'info';
  severity: 'high' | 'medium' | 'low';
  title: string;
  message: string;
  nodeId?: string;
  suggestion?: string;
}

// =============================================
// FUNÇÃO PRINCIPAL DE VALIDAÇÃO
// =============================================

export function validateFlow(nodes: Node[], edges: Edge[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Verifica se o fluxo está vazio
  if (nodes.length === 0) {
    issues.push({
      id: 'empty-flow',
      type: 'info',
      severity: 'low',
      title: 'Fluxo vazio',
      message: 'O fluxo não contém nenhum nó. Adicione nós para começar.',
      suggestion: 'Clique no botão "+" ou arraste nós do painel lateral'
    });
    return issues;
  }

  // Verifica nós de início
  const startNodes = nodes.filter(n => n.type === 'start');
  if (startNodes.length === 0) {
    issues.push({
      id: 'no-start-node',
      type: 'error',
      severity: 'high',
      title: 'Sem nó de início',
      message: 'O fluxo deve ter pelo menos um nó de início.',
      suggestion: 'Adicione um nó "Início" para definir o ponto de partida'
    });
  } else if (startNodes.length > 1) {
    issues.push({
      id: 'multiple-start-nodes',
      type: 'warning',
      severity: 'medium',
      title: 'Múltiplos nós de início',
      message: `O fluxo tem ${startNodes.length} nós de início. Apenas o primeiro será executado.`,
      suggestion: 'Remova os nós de início extras ou crie fluxos separados'
    });
  }

  // Verifica nós órfãos (sem conexões)
  nodes.forEach(node => {
    const hasIncoming = edges.some(e => e.target === node.id);
    const hasOutgoing = edges.some(e => e.source === node.id);

    if (!hasIncoming && !hasOutgoing && node.type !== 'start' && node.type !== 'webhook_trigger' && node.type !== 'scheduler') {
      issues.push({
        id: `orphan-node-${node.id}`,
        type: 'warning',
        severity: 'medium',
        title: 'Nó isolado',
        message: `O nó "${node.data.label || node.type}" não está conectado a nenhum outro nó.`,
        nodeId: node.id,
        suggestion: 'Conecte este nó ao fluxo ou remova-o'
      });
    }

    // Verifica nós sem saída (exceto nós de fim)
    if (hasIncoming && !hasOutgoing && node.type !== 'end') {
      issues.push({
        id: `dead-end-${node.id}`,
        type: 'warning',
        severity: 'low',
        title: 'Nó sem saída',
        message: `O nó "${node.data.label || node.type}" não tem conexões de saída.`,
        nodeId: node.id,
        suggestion: 'Adicione conexão para o próximo nó ou adicione um nó "Fim"'
      });
    }
  });

  // Verifica nós de condição
  const conditionNodes = nodes.filter(n => n.type === 'condition');
  conditionNodes.forEach(node => {
    const outgoingEdges = edges.filter(e => e.source === node.id);
    const hasTruePath = outgoingEdges.some(e => e.sourceHandle === 'true');
    const hasFalsePath = outgoingEdges.some(e => e.sourceHandle === 'false');

    if (!hasTruePath || !hasFalsePath) {
      issues.push({
        id: `incomplete-condition-${node.id}`,
        type: 'error',
        severity: 'high',
        title: 'Condição incompleta',
        message: `A condição "${node.data.label || 'Sem nome'}" não tem ambos os caminhos (verdadeiro e falso).`,
        nodeId: node.id,
        suggestion: 'Conecte ambos os caminhos da condição'
      });
    }

    // Verifica se a condição tem variável configurada
    if (!node.data.variable) {
      issues.push({
        id: `missing-condition-var-${node.id}`,
        type: 'error',
        severity: 'high',
        title: 'Condição sem variável',
        message: `A condição "${node.data.label || 'Sem nome'}" não tem variável configurada.`,
        nodeId: node.id,
        suggestion: 'Configure a variável e o operador da condição'
      });
    }
  });

  // Verifica nós de input
  const inputNodes = nodes.filter(n => n.type === 'input');
  inputNodes.forEach(node => {
    if (!node.data.variable) {
      issues.push({
        id: `missing-input-var-${node.id}`,
        type: 'error',
        severity: 'high',
        title: 'Input sem variável',
        message: `O input "${node.data.label || 'Sem nome'}" não tem variável configurada.`,
        nodeId: node.id,
        suggestion: 'Configure a variável onde o input será armazenado'
      });
    }

    if (!node.data.message) {
      issues.push({
        id: `missing-input-message-${node.id}`,
        type: 'warning',
        severity: 'medium',
        title: 'Input sem mensagem',
        message: `O input "${node.data.label || 'Sem nome'}" não tem mensagem configurada.`,
        nodeId: node.id,
        suggestion: 'Adicione uma mensagem para solicitar o input do usuário'
      });
    }
  });

  // Verifica nós de mensagem
  const messageNodes = nodes.filter(n => n.type === 'message');
  messageNodes.forEach(node => {
    if (!node.data.message || node.data.message.trim() === '') {
      issues.push({
        id: `empty-message-${node.id}`,
        type: 'error',
        severity: 'high',
        title: 'Mensagem vazia',
        message: `O nó de mensagem "${node.data.label || 'Sem nome'}" está vazio.`,
        nodeId: node.id,
        suggestion: 'Adicione o conteúdo da mensagem'
      });
    }
  });

  // Verifica nós HTTP Request
  const httpNodes = nodes.filter(n => n.type === 'http_request');
  httpNodes.forEach(node => {
    if (!node.data.url) {
      issues.push({
        id: `missing-http-url-${node.id}`,
        type: 'error',
        severity: 'high',
        title: 'HTTP Request sem URL',
        message: `O HTTP Request "${node.data.label || 'Sem nome'}" não tem URL configurada.`,
        nodeId: node.id,
        suggestion: 'Configure a URL da requisição'
      });
    }
  });

  // Verifica loops
  const loopNodes = nodes.filter(n => n.type === 'loop');
  loopNodes.forEach(node => {
    const loopBodyEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'loop-body');
    const loopExitEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'loop-exit');

    if (!loopBodyEdge) {
      issues.push({
        id: `missing-loop-body-${node.id}`,
        type: 'error',
        severity: 'high',
        title: 'Loop sem corpo',
        message: `O loop "${node.data.label || 'Sem nome'}" não tem corpo configurado.`,
        nodeId: node.id,
        suggestion: 'Conecte o handle "body" do loop ao próximo nó'
      });
    }

    if (!loopExitEdge) {
      issues.push({
        id: `missing-loop-exit-${node.id}`,
        type: 'warning',
        severity: 'medium',
        title: 'Loop sem saída',
        message: `O loop "${node.data.label || 'Sem nome'}" não tem saída configurada.`,
        nodeId: node.id,
        suggestion: 'Conecte o handle "exit" do loop ao próximo nó após o loop'
      });
    }
  });

  // Verifica ciclos infinitos simples (a -> b -> a)
  const visitedPaths = new Set<string>();
  const checkCycles = (nodeId: string, path: string[]): boolean => {
    if (path.includes(nodeId)) {
      return true; // Ciclo detectado
    }

    const newPath = [...path, nodeId];
    const pathKey = newPath.join('->');

    if (visitedPaths.has(pathKey)) {
      return false;
    }
    visitedPaths.add(pathKey);

    const outgoing = edges.filter(e => e.source === nodeId);
    for (const edge of outgoing) {
      if (checkCycles(edge.target, newPath)) {
        return true;
      }
    }

    return false;
  };

  startNodes.forEach(startNode => {
    if (checkCycles(startNode.id, [])) {
      issues.push({
        id: 'cyclic-flow',
        type: 'warning',
        severity: 'low',
        title: 'Possível ciclo infinito',
        message: 'O fluxo pode conter um ciclo infinito. Verifique as conexões.',
        suggestion: 'Adicione condições de saída ou use o nó "Loop" para ciclos controlados'
      });
    }
  });

  // Análise de complexidade
  if (nodes.length > 50) {
    issues.push({
      id: 'high-complexity',
      type: 'info',
      severity: 'low',
      title: 'Fluxo complexo',
      message: `O fluxo tem ${nodes.length} nós. Considere dividir em fluxos menores.`,
      suggestion: 'Divida em sub-fluxos ou use templates para organizar melhor'
    });
  }

  return issues;
}

// =============================================
// HELPER: GET FLOW HEALTH SCORE
// =============================================

export function getFlowHealthScore(issues: ValidationIssue[]): {
  score: number;
  status: 'excellent' | 'good' | 'fair' | 'poor';
  color: string;
} {
  const errorCount = issues.filter(i => i.type === 'error').length;
  const warningCount = issues.filter(i => i.type === 'warning').length;

  const errorWeight = 20;
  const warningWeight = 5;

  const deductions = (errorCount * errorWeight) + (warningCount * warningWeight);
  const score = Math.max(0, Math.min(100, 100 - deductions));

  let status: 'excellent' | 'good' | 'fair' | 'poor';
  let color: string;

  if (score >= 90) {
    status = 'excellent';
    color = '#10b981'; // green
  } else if (score >= 70) {
    status = 'good';
    color = '#3b82f6'; // blue
  } else if (score >= 50) {
    status = 'fair';
    color = '#f59e0b'; // amber
  } else {
    status = 'poor';
    color = '#ef4444'; // red
  }

  return { score, status, color };
}
